import {
    useMemo,
    type FC,
    useRef
} from "react";
import {
    Pressable,
    Animated,
    View
} from "react-native";
import type IMarkerProps from "./type";
import type {
    MarkerContextType
} from "./type";
import stylesheet, {
    getMarkerColors,
    getMarkerSize,
    useStyles
} from "./stylesheet";
import MarkerContent from "./components/content";
import MarkerIcon from "./components/icon";
import {
    MarkerContext
} from "./context";
import {
    useMarkerBreathe,
    useMarkerStuck
} from "./util";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    useAnimatedState,
    useReducedMotion
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    ChevronRight as ChevronRightIcon
} from "lucide-react-native";
import DisclosureChevron from "../collapsible/internal/disclosureChevron";
import StateLayer from "../stateLayer";
import Icon from "../icon";

const Marker: FC<IMarkerProps> = ({
    isShowChevron = true,
    isStuck: isStuckProp,
    isCentered = false,
    variant = "inline",
    isPending = false,
    isSticky = false,
    isUnread = false,
    type = "neutral",
    size = "medium",
    onStuckChange,
    customTheme,
    isExpanded,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    children,
    onFocus,
    onPress,
    onBlur,
    style,
    icon,
    role,
    ...props
}) => {
    const {
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        typography,
        durations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const isReduced = useReducedMotion();

    const node = useRef<View>(null);

    const isPressable = !!onPress && variant !== "separator";

    const isCurrentPending = isPending && !isPressable;

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const isStuck = useMarkerStuck({
        top: spaces.spacingSm,
        isStuck: isStuckProp,
        onStuckChange,
        isSticky,
        node
    }) && variant === "separator";

    const stuckProgress = useAnimatedState({
        value: isStuck ? 1 : 0,
        intent: "contentSwap",
        motion: "fade",
        customTheme
    });

    const breathe = useMarkerBreathe({
        isPending: isCurrentPending,
        durations,
        isReduced
    });

    const currentSize = useMemo(() => getMarkerSize({
        typography,
        variant,
        size
    }), [
        typography,
        variant,
        size
    ]);

    const baseColors = useMemo(() => getMarkerColors({
        isUnread,
        variant,
        colors,
        type
    }), [
        isUnread,
        variant,
        colors,
        type
    ]);

    const isEmphasized = isPressable ? isHovered || isPressed : isCurrentPending && isReduced;

    const currentColors = useMemo(() => isEmphasized ? {
        ...baseColors,
        textColor: baseColors.emphasizedColor
    } : baseColors, [
        isEmphasized,
        baseColors
    ]);

    const {
        pillSurface: pillSurfaceDynamicStyle,
        container: containerDynamicStyle,
        pressable: pressableDynamicStyle,
        iconSlot: iconSlotDynamicStyle,
        surface: surfaceDynamicStyle,
        line: lineDynamicStyle,
        pill: pillDynamicStyle
    } = useStyles({
        isSticky: isSticky && variant === "separator",
        isFocusVisible,
        currentColors,
        shapeConfigs,
        touchTargets,
        currentSize,
        pointerType,
        shadowAlpha,
        elevations,
        isCentered,
        radiuses,
        borders,
        variant,
        colors,
        spaces
    });

    const context: MarkerContextType = useMemo(() => ({
        typography: currentSize.typography,
        lineHeight: currentSize.lineHeight,
        isPending: isCurrentPending,
        iconSize: iconSizes.small,
        currentColors,
        customTheme,
        isReduced,
        breathe,
        isStuck,
        size
    }), [
        currentSize.typography,
        currentSize.lineHeight,
        isCurrentPending,
        iconSizes.small,
        currentColors,
        customTheme,
        isReduced,
        breathe,
        isStuck,
        size
    ]);

    const currentRole = role ?? (isCurrentPending ? "status" : undefined);

    const renderChildren = () => {
        if(typeof children === "string" || typeof children === "number") {
            return <MarkerContent>
                {children}
            </MarkerContent>;
        }

        return children;
    };

    const renderIcon = () => {
        if(!icon) {
            return null;
        }

        return <MarkerIcon
            customTheme={customTheme}
            icon={icon}
        />;
    };

    const renderChevron = () => {
        if(!isShowChevron) {
            return null;
        }

        if(isExpanded !== undefined) {
            return <View
                style={[
                    stylesheet.iconSlot,
                    iconSlotDynamicStyle
                ]}
                aria-hidden={true}
            >
                <DisclosureChevron
                    color={colors.content.icon.low}
                    customTheme={customTheme}
                    size={iconSizes.small}
                    isOpen={isExpanded}
                />
            </View>;
        }

        return <View
            style={[
                stylesheet.iconSlot,
                iconSlotDynamicStyle
            ]}
            aria-hidden={true}
        >
            <Icon
                customTheme={customTheme}
                icon={ChevronRightIcon}
                size={iconSizes.small}
                color="low"
            />
        </View>;
    };

    const renderSeparatorLine = () => {
        return <Animated.View
            style={[
                stylesheet.line,
                lineDynamicStyle,
                isStuck && stylesheet.lineStuck,
                {
                    opacity: stuckProgress.interpolate({
                        outputRange: [
                            1,
                            0
                        ],
                        inputRange: [
                            0,
                            1
                        ]
                    })
                }
            ]}
            aria-hidden={true}
        />;
    };

    const renderSeparator = () => {
        return <>
            {renderSeparatorLine()}
            <View
                style={[
                    stylesheet.pill,
                    pillDynamicStyle
                ]}
            >
                <Animated.View
                    style={[
                        stylesheet.pillSurface,
                        pillSurfaceDynamicStyle,
                        {
                            opacity: stuckProgress
                        }
                    ]}
                    aria-hidden={true}
                />
                {renderChildren()}
            </View>
            {renderSeparatorLine()}
        </>;
    };

    const renderPressable = () => {
        return <Pressable
            {...props}
            {...interactionHandlers}
            aria-expanded={isExpanded}
            style={[
                style,
                stylesheet.pressable,
                pressableDynamicStyle
            ]}
            role={role ?? "button"}
            onPress={onPress}
        >
            <View
                style={[
                    stylesheet.surface,
                    surfaceDynamicStyle
                ]}
            >
                <StateLayer
                    borderRadius={radiuses.actions}
                    customTheme={customTheme}
                    isPressed={isPressed}
                    isHovered={isHovered}
                />
                {renderIcon()}
                {renderChildren()}
                {renderChevron()}
            </View>
        </Pressable>;
    };

    if(isPressable) {
        return <MarkerContext.Provider
            value={context}
        >
            {
                variant === "border" ?
                    <View
                        style={[
                            stylesheet.container,
                            containerDynamicStyle
                        ]}
                    >
                        {renderPressable()}
                    </View>
                :
                    renderPressable()
            }
        </MarkerContext.Provider>;
    }

    return <MarkerContext.Provider
        value={context}
    >
        <View
            {...props}
            style={[
                style,
                variant === "separator" ? stylesheet.separator : stylesheet.container,
                containerDynamicStyle
            ]}
            role={currentRole}
            ref={node}
        >
            {
                variant === "separator" ?
                    renderSeparator()
                :
                    <>
                        {renderIcon()}
                        {renderChildren()}
                    </>
            }
        </View>
    </MarkerContext.Provider>;
};
export default Object.assign(Marker, {
    Content: MarkerContent,
    Icon: MarkerIcon
});
